/**
 * usePermissions Hook
 * 
 * Single source of truth for all permission and role checks in the frontend.
 * 
 * Updated: 2026-01-26 - Permission System V2
 * - Now supports primary_role_id from role_templates
 * - Supports merged permissions from primary + secondary roles
 * - Menu visibility derived from role permissions
 * 
 * Usage:
 *   import { usePermissions } from '../hooks/usePermissions';
 *   
 *   const MyComponent = ({ user }) => {
 *     const permissions = usePermissions(user);
 *     
 *     if (permissions.canAccessAdminPanel) { ... }
 *     if (permissions.can('matrix.upload')) { ... }
 *     if (permissions.canSeeMenuItem('progressTrackers')) { ... }
 *   };
 */

import { useMemo } from 'react';
import { 
  getRoleColor as getRoleColorFromConfig, 
  getRoleLevel as getRoleLevelFromConfig,
  ROLE_HIERARCHY as CONFIG_ROLE_HIERARCHY,
  ROLE_COLORS as CONFIG_ROLE_COLORS,
  ELEVATED_ROLES as CONFIG_ELEVATED_ROLES,
  ADMIN_PANEL_ROLES as CONFIG_ADMIN_PANEL_ROLES,
  USER_MANAGEMENT_ROLES as CONFIG_USER_MANAGEMENT_ROLES
} from './useRoleConfig';
import { canAccessResource, canAccessSubDepartmentResource } from '../config/resourceAccess';

// Protected superadmin emails
const PROTECTED_SUPERADMINS = [
  'jeamon@laframboise.net',
  'yrezaei@laframboise.net'
];

// Authority levels (lower index = more authority)
const AUTHORITY_LEVELS = ['System', 'Upper Management', 'Manager', 'Lead', 'Staff'];

// Re-export from useRoleConfig for backward compatibility
const ROLE_HIERARCHY = CONFIG_ROLE_HIERARCHY;
const ROLE_COLORS = CONFIG_ROLE_COLORS;
const ELEVATED_ROLES = CONFIG_ELEVATED_ROLES;
const ADMIN_PANEL_ROLES = CONFIG_ADMIN_PANEL_ROLES;
const USER_MANAGEMENT_ROLES = CONFIG_USER_MANAGEMENT_ROLES;

/**
 * Get the color for a role name
 * Uses centralized config from useRoleConfig (with fallbacks)
 * @param {string} role - Role name
 * @returns {string} - Hex color code
 */
export const getRoleColor = (role) => {
  return getRoleColorFromConfig(role);
};

/**
 * Get the hierarchy level for a role name (lower = more privileged)
 * Uses centralized config from useRoleConfig (with fallbacks)
 * @param {string} role - Role name
 * @returns {number} - Hierarchy level
 */
export const getRoleLevel = (role) => {
  return getRoleLevelFromConfig(role);
};

/**
 * usePermissions hook - provides all permission checking functionality
 * 
 * @param {Object} user - User object with roles/permissions or primary_role_id (V2)
 * @returns {Object} - Permission checking object
 */
export const usePermissions = (user) => {
  return useMemo(() => {
    // V2: Support primary_role_id from new schema
    let roleName, roleId, authorityLevel, hierarchyLevel, departmentId, scope, menuItems;
    let permissionsSet = new Set();
    
    // Track if authority is overridden (for display purposes)
    let userAuthorityOverride = null;
    let roleDefaultAuthority = null;
    let subDepartments = new Set();
    
    // Track all accessible department IDs (primary + secondary roles)
    let allDepartmentIds = new Set();

    if (user?.primary_role_id) {
      // V2 user with role template
      roleName = user.primary_role_id;
      roleId = user.primary_role_id;
      
      // V2 Decoupled Authority: User override takes precedence, then role default
      // effective_authority_level is computed in backend as COALESCE(user.authority, role.authority)
      authorityLevel = user.effective_authority_level || 
                       user.user_authority_level || 
                       user.role_default_authority || 
                       user.role_authority_level || 
                       'Staff';
      hierarchyLevel = user.effective_authority_hierarchy || 
                       user.user_authority_hierarchy ||
                       user.role_hierarchy_level || 
                       100;
      
      // Track authority override status
      userAuthorityOverride = user.user_authority_level || null;
      roleDefaultAuthority = user.role_default_authority || user.role_authority_level;
      
      departmentId = user.role_department_id || user.department_id || null;
      scope = user.role_scope || user.scope || 'assigned_projects';
      
      // Merged permissions from primary + secondary roles
      if (user.merged_permissions && Array.isArray(user.merged_permissions)) {
        permissionsSet = new Set(user.merged_permissions);
      } else if (user.role_permissions && Array.isArray(user.role_permissions)) {
        permissionsSet = new Set(user.role_permissions);
      }
      
      // Menu items from role template
      if (user.merged_menu_items && Array.isArray(user.merged_menu_items)) {
        menuItems = user.merged_menu_items;
      } else if (user.role_menu_items && Array.isArray(user.role_menu_items)) {
        menuItems = user.role_menu_items;
      } else {
        menuItems = [];
      }
      
      // Sub-departments for module access filtering
      if (user.sub_departments && Array.isArray(user.sub_departments)) {
        subDepartments = new Set(user.sub_departments.map(sd => sd.sub_department_id || sd.id));
      }
      
      // Build set of all accessible department IDs (primary + secondary roles)
      if (departmentId) {
        allDepartmentIds.add(departmentId);
      }
      // Add departments from secondary roles
      if (user.secondary_roles && Array.isArray(user.secondary_roles)) {
        user.secondary_roles.forEach(sr => {
          if (sr.department_id) {
            allDepartmentIds.add(sr.department_id);
          }
        });
      }
    } else {
      // No valid role - user needs role assignment
      roleName = null;
      roleId = null;
      authorityLevel = null;
      hierarchyLevel = 999;
      departmentId = null;
      scope = 'assigned_projects';
      menuItems = [];
      permissionsSet = new Set();
    }
    
    // Role color from user or config
    const roleColor = user?.role_color || getRoleColorFromConfig(roleName);
    
    // Display name
    const roleDisplayName = user?.role_display_name || 
      (roleName ? roleName.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : 'Unknown');
    
    // Helper to check elevated based on authority or role list
    const isElevated = () => {
      if (authorityLevel) {
        const elevatedLevels = ['System', 'Upper Management', 'Manager'];
        return elevatedLevels.includes(authorityLevel);
      }
      if (hierarchyLevel <= 15) return true;
      return ELEVATED_ROLES.includes(roleName);
    };
    
    // Helper to check admin panel access - SUPERADMIN ONLY
    const canAccessAdmin = () => {
      // Only superadmins (role name or System authority level) can access admin panel
      return roleName === 'superadmin' || authorityLevel === 'System';
    };
    
    // Helper to check user management - SUPERADMIN ONLY
    const canManage = () => {
      // Only superadmins can manage users
      return roleName === 'superadmin' || authorityLevel === 'System';
    };
    
    return {
      // =====================
      // Permission Checks
      // =====================
      
      /**
       * Check if user has a specific permission
       * @param {string} permissionName - Permission name (e.g., 'matrix.upload' or 'view_all')
       * @returns {boolean}
       */
      can: (permissionName) => {
        if (permissionsSet.has('*')) return true;
        return permissionsSet.has(permissionName);
      },
      
      /**
       * Check if user has any of the specified permissions
       * @param {string[]} permissionNames - Array of permission names
       * @returns {boolean}
       */
      canAny: (permissionNames) => {
        if (permissionsSet.has('*')) return true;
        return permissionNames.some(p => permissionsSet.has(p));
      },
      
      /**
       * Check if user has all of the specified permissions
       * @param {string[]} permissionNames - Array of permission names
       * @returns {boolean}
       */
      canAll: (permissionNames) => {
        if (permissionsSet.has('*')) return true;
        return permissionNames.every(p => permissionsSet.has(p));
      },
      
      // =====================
      // Menu Visibility (V2)
      // =====================
      
      /**
       * Check if user can see a specific menu item
       * @param {string} menuItemId - Menu item ID
       * @returns {boolean}
       */
      canSeeMenuItem: (menuItemId) => {
        if (menuItems.includes('*')) return true;
        return menuItems.includes(menuItemId);
      },
      
      /** Get all menu items the user can see */
      menuItems,
      
      // =====================
      // Role Checks (Individual)
      // =====================
      
      /** Is user a superadmin? */
      isSuperadmin: roleName === 'superadmin' || authorityLevel === 'System',
      
      /** Is user an admin (includes superadmin)? */
      isAdmin: ['admin', 'superadmin', 'construction_owner', 'it_lead'].includes(roleName) 
               || authorityLevel === 'System',
      
      /** Is user a PM? */
      isPM: roleName === 'pm' || roleName === 'construction_project_manager',
      
      /** Is user a Cost Control? */
      isCostControl: roleName === 'cost_control' || roleName === 'accounting_cost_clerk',
      
      /** Is user a TC? */
      isTC: roleName === 'tc' || roleName === 'construction_technical_coordinator',

      /** Hiring / Layoff — Accounting department or superadmin only */
      canAccessHiringDatabase: roleName === 'superadmin' || authorityLevel === 'System' || allDepartmentIds.has(2),

      /** Project schedule writes — TCs are view-only */
      canEditSchedule: roleName === 'superadmin' || authorityLevel === 'System'
        || !(roleName === 'tc' || roleName === 'construction_technical_coordinator'),
      
      /** Is user a QS? */
      isQS: roleName === 'qs',
      
      /** Is user a Supervisor? */
      isSupervisor: roleName === 'supervisor' || roleName === 'construction_superintendent',
      
      /** Is user a viewer? */
      isViewer: roleName === 'viewer',
      
      // =====================
      // Composite Role Checks
      // =====================
      
      /** 
       * Is user elevated? (based on authority level or role)
       * Elevated users can perform matrix uploads, exports, etc.
       */
      isElevated: isElevated(),
      
      /**
       * Can user access the admin panel?
       */
      canAccessAdminPanel: canAccessAdmin(),
      
      /**
       * Can user manage users and assign roles?
       */
      canManageUsers: canManage(),
      
      /**
       * Can user assign roles to other users?
       */
      canAssignRoles: canManage(),
      
      /**
       * Can user manage employees roster?
       */
      canManageEmployees: canManage(),
      
      // =====================
      // Role Comparison
      // =====================
      
      /**
       * Check if user has a specific role
       * @param {string} targetRole - Role name to check
       * @returns {boolean}
       */
      hasRole: (targetRole) => roleName === targetRole?.toLowerCase(),
      
      /**
       * Check if user has any of the specified roles
       * @param {string[]} targetRoles - Array of role names
       * @returns {boolean}
       */
      hasAnyRole: (targetRoles) => targetRoles.some(r => roleName === r?.toLowerCase()),
      
      /**
       * Check if user's role is at least as privileged as the target role
       * @param {string} targetRole - Role to compare against
       * @returns {boolean}
       */
      isAtLeast: (targetRole) => {
        const userLevel = hierarchyLevel ?? (ROLE_HIERARCHY[roleName] ?? 999);
        const targetLevel = ROLE_HIERARCHY[targetRole?.toLowerCase()] ?? 999;
        return userLevel <= targetLevel;
      },
      
      /**
       * Check if user's role is more privileged than the target role
       * @param {string} targetRole - Role to compare against
       * @returns {boolean}
       */
      isMorePrivilegedThan: (targetRole) => {
        const userLevel = hierarchyLevel ?? (ROLE_HIERARCHY[roleName] ?? 999);
        const targetLevel = ROLE_HIERARCHY[targetRole?.toLowerCase()] ?? 999;
        return userLevel < targetLevel;
      },
      
      // =====================
      // Role Display Properties
      // =====================
      
      /** User's role name (role template ID) */
      roleName,
      
      /** User's role ID */
      roleId,
      
      /** User's role hierarchy level (lower = more privileged) */
      roleLevel: hierarchyLevel,
      
      /** Color for the user's role badge */
      roleColor,
      
      /** Display name for the role */
      roleDisplayName,
      
      /** Authority level (V2) - Effective authority after override resolution */
      authorityLevel,
      
      /** User's explicit authority override (null = using role default) */
      userAuthorityOverride,
      
      /** Role's default authority level */
      roleDefaultAuthority,
      
      /** Is authority level overridden from role default? */
      isAuthorityOverridden: userAuthorityOverride !== null,
      
      /** Get effective authority info (for display) */
      getEffectiveAuthority: () => ({
        name: authorityLevel,
        hierarchyLevel,
        isOverride: userAuthorityOverride !== null,
        roleDefault: roleDefaultAuthority
      }),
      
      /** Department ID (V2) */
      departmentId,
      
      /** Department name (V2) */
      departmentName: user?.role_department_name || user?.department_name || null,
      
      /** Permission scope (V2) */
      scope,
      
      /** All permission names the user has */
      permissions: Array.from(permissionsSet),
      
      /** Sub-departments the user is assigned to */
      subDepartments: Array.from(subDepartments),
      
      /** Check if user is assigned to a specific sub-department */
      hasSubDepartment: (subDeptId) => subDepartments.has(subDeptId),
      
      /**
       * Check permission with sub-department filtering
       * @param {string} permissionName - Permission to check
       * @param {number|null} subDeptId - Required sub-department (null = no sub-dept required)
       * @returns {boolean}
       */
      canAccessWithSubDept: (permissionName, subDeptId = null) => {
        // Superadmin bypasses all checks
        if (roleName === 'superadmin' || authorityLevel === 'System') return true;
        // Must have the base permission
        if (!permissionsSet.has('*') && !permissionsSet.has(permissionName)) return false;
        // If sub-department required, must be assigned
        if (subDeptId !== null && !subDepartments.has(subDeptId)) return false;
        return true;
      },
      
      // =====================
      // User State
      // =====================
      
      /** Does user need role assignment? */
      needsRoleAssignment: user?.needs_role_assignment === true,
      
      /** Is the user object valid/loaded? */
      isLoaded: !!user,
      
      /** Is the user a protected superadmin? */
      isProtectedSuperadmin: PROTECTED_SUPERADMINS.includes(user?.email?.toLowerCase()),
      
      /** Was user provisioned (pre-created with email+role)? */
      isProvisioned: !!user?.provisioned_by,
      
      /** Secondary roles (V2) */
      secondaryRoles: user?.secondary_roles || [],
      
      // =====================
      // Department-Based Access (V2)
      // =====================
      
      /**
       * Get all department IDs the user has access to (primary + secondary roles)
       * @returns {Set<number>} Set of department IDs
       */
      getAllDepartmentIds: () => allDepartmentIds,
      
      /**
       * Get all department IDs as an array
       * @returns {number[]} Array of department IDs
       */
      allDepartmentIds: Array.from(allDepartmentIds),
      
      /**
       * Check if user can access a department-restricted resource
       * Uses resourceAccess.js configuration to determine required departments
       * @param {string} resourceId - Resource/menu item ID to check
       * @returns {boolean} True if user can access the resource
       */
      canAccessDepartmentResource: (resourceId) => {
        // Superadmin bypasses all department checks
        if (roleName === 'superadmin' || authorityLevel === 'System') return true;
        // Use the resource access helper
        return canAccessResource(resourceId, allDepartmentIds);
      },
      
      /**
       * Check if user has access to a specific department
       * @param {number} deptId - Department ID to check
       * @returns {boolean} True if user has access to the department
       */
      hasDepartment: (deptId) => {
        if (roleName === 'superadmin' || authorityLevel === 'System') return true;
        return allDepartmentIds.has(deptId);
      },
      
      // =====================
      // Sub-Department Access (V2)
      // =====================
      
      /**
       * Check if user can access a sub-department-restricted resource
       * Uses resourceAccess.js configuration to determine required sub-departments
       * Primarily used for Progress Tracking discipline access control
       * @param {string} resourceId - Resource/menu item ID to check
       * @returns {boolean} True if user can access the resource
       */
      canAccessSubDepartmentResource: (resourceId) => {
        // Superadmin bypasses all sub-department checks
        if (roleName === 'superadmin' || authorityLevel === 'System') return true;
        // Use the resource access helper with user's sub-departments
        return canAccessSubDepartmentResource(resourceId, subDepartments);
      },
    };
  }, [user]);
};

// Export constants for use elsewhere
export { 
  ROLE_HIERARCHY, 
  ROLE_COLORS, 
  ELEVATED_ROLES, 
  ADMIN_PANEL_ROLES, 
  USER_MANAGEMENT_ROLES,
  PROTECTED_SUPERADMINS,
  AUTHORITY_LEVELS
};

// Default export
export default usePermissions;
